import { Box, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { memo } from "react";
import { FONT_SIZE } from "../../../../theme/tokens";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";

const formatHours = (value: number): string => (Number.isInteger(value) ? value.toString() : value.toFixed(1));

/**
 * Renders the marginal overtime for adding/removing a nurse: the resulting weekly OT total with the delta in
 * parentheses, and a current/additional(or removed)/total breakdown. When `showWeeklyOt` is false it degrades
 * to just the delta hours (used where no weekly-before figure is available).
 *
 * Layout: on desktop (default) the breakdown lives in a hover tooltip. On touch surfaces (mobile cards) there
 * is no hover, so pass `detailed` to render that same breakdown inline on one line, wrapping left if it can't fit.
 *
 * Promoted from `NursesMatrixGrid/columns.tsx` so the desktop grid, the approval popup, and both mobile cards
 * render OT identically. Kept prop-driven and i18n-injected (`t` passed in) so it stays a pure, shareable
 * UIToolkit cell with no data-fetching of its own.
 */
export const OvertimeCell = memo(function OvertimeCell({
	overtimeHours,
	weeklyOvertimeHoursBeforeChange,
	showWeeklyOt,
	isAdd,
	t,
	detailed = false,
	dataTestId,
}: {
	overtimeHours: number;
	weeklyOvertimeHoursBeforeChange: number | undefined;
	showWeeklyOt: boolean;
	isAdd: boolean;
	t: TFunction;
	/** Render the current/additional/total breakdown inline (no hover tooltip) — for touch surfaces. */
	detailed?: boolean;
	/** Stable selector for tests, forwarded to this cell's outermost element in every render path. */
	dataTestId?: string;
}) {
	const delta = overtimeHours || 0;
	// Bare-delta path renders text only, so there is no element to hang the testid on — introduce a
	// wrapper solely when a caller asked for one, keeping the DOM unchanged otherwise.
	if (!showWeeklyOt) {
		const hours = formatHours(delta);
		return dataTestId ? <span data-testid={dataTestId}>{hours}</span> : hours;
	}
	const weekly = weeklyOvertimeHoursBeforeChange ?? 0;
	const totalAfter = Math.max(0, isAdd ? weekly + delta : weekly - delta);
	const deltaSign = isAdd ? "+" : "-";
	const currentLabel = t("nurses.matrix.ot.tooltip.current", { value: formatHours(weekly) });
	const deltaLabel = isAdd
		? t("nurses.matrix.ot.tooltip.additional", { value: formatHours(delta) })
		: t("nurses.matrix.ot.tooltip.removed", { value: formatHours(delta) });
	const totalLabel = t("nurses.matrix.ot.tooltip.total", { value: formatHours(totalAfter) });

	// `value` is the outermost node in the tooltip path but nested inside a wrapper in the detailed path,
	// so the testid lands on whichever of the two is actually outermost.
	const value = (
		<Box data-testid={detailed ? undefined : dataTestId} sx={{ display: "flex", gap: 0.5, justifyContent: "center", alignItems: "baseline" }}>
			<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS }}>{formatHours(totalAfter)}</Typography>
			{delta > 0 && (
				<Typography sx={{ fontSize: FONT_SIZE.CAPTION, opacity: 0.75 }}>
					({deltaSign}
					{formatHours(delta)})
				</Typography>
			)}
		</Box>
	);

	// Touch surfaces: no hover tooltip, so surface the same current + additional breakdown inline on one
	// line, wrapping to the left if it doesn't fit.
	if (detailed) {
		return (
			<Box data-testid={dataTestId} sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "flex-start", gap: 0.5 }}>
				{value}
				{delta > 0 && (
					<Typography sx={{ fontSize: FONT_SIZE.CAPTION, opacity: 0.75 }}>
						{currentLabel} · {deltaLabel}
					</Typography>
				)}
			</Box>
		);
	}

	return (
		<InHouseTooltip
			slotProps={{ tooltip: { sx: { fontSize: FONT_SIZE.SECONDARY } } }}
			title={
				<>
					{currentLabel}
					<br />
					{deltaLabel}
					<br />
					{totalLabel}
				</>
			}
		>
			{value}
		</InHouseTooltip>
	);
});
